
import { google } from 'google-maps';
import PathMap from '@/components/FieldAndPath/PathMap.vue';
import FieldAndPathTabs from '@/components/FieldAndPath/FieldAndPathTabs.vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import PathStore from '../store/modules/paths';
import { Mixins, Watch } from 'vue-property-decorator';
import TabNames from '@/services/tabNames';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Guid } from 'guid-typescript';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { SimplifyLL, ISimplifyLatLongPoint } from 'simplify-ts';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import MqttMixIn from '@/mixins/mqttManagement';
import NotificationMixIn from '@/mixins/notifications';
import { PubSub } from '@/PubSubClass';
import { Convert as GenericNotifyConvert } from '@/flightpath-components/models/mqttPayload/GenericNotificationMessage';
import { NotificationMessageType } from '@/flightpath-components/models/NotificationMessageType';
import { SortHelper } from '@/services/SortHelper';
import LastModifiedMixIn from '@/mixins/lastModified';
import { AltitudeFinder } from '@/utils/AltitudeFinder';
import { PathHelper } from '@/utils/PathHelper';
import { GPSLocation } from '@/flightpath-components/models/GPSLocation';
import GlobalStore from '@/store/modules/global';

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'Paths', inject: ['container'], components: { PathMap, FieldAndPathTabs } })
export default class Paths extends Mixins(MqttMixIn, NotificationMixIn, LastModifiedMixIn) {
  public pathStore = getModule(PathStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  tabName = TabNames.Paths;
  type = [NotificationMessageType.NewPath, NotificationMessageType.ModifiedPath];
  simplifyFactor = 1;
  metersToDegreesFactor = 9.09e-6;
  panel = [0];
  editMode = false;
  pathSelected = false;
  addPathMode = false;
  editWorkAreaMode = false;
  showWorkAreaControls = false;
  editCropAreaMode = false;
  showCropAreaControls = false;
  plannedPath: SimplePath[] = [];
  emptyPath: SimplePath = new SimplePath();
  selectedPath = this.emptyPath;
  selectedPathLength = 0;
  selectedWorkAreaArea = 0;
  selectedCropAreaArea = 0;
  emptyField: FieldOperationsModel = new FieldOperationsModel();

  selectedField = this.emptyField;
  newPathName = '';
  fields: FieldOperationsModel[] = [];
  loading = true;
  isSimplifyingPath = false;
  tempWorkArea: google.maps.LatLng[] = [];
  tempCropArea: google.maps.LatLng[] = [];
  deleteWorkAreaDialog = false;
  deleteCropAreaDialog = false;
  deletePathDialog = false;
  selectedFile: File | null = null;
  windowSize = {
    x: 0,
    y: 0
  };

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  onFileChanged(e: any) {
    this.selectedFile = e.target.files[0];
    this.validateUpload(this.selectedFile as File);
  }

  onUploadPath() {
    if (this.$refs.uploader == undefined) return;
    if (this.$refs.uploader instanceof HTMLElement) {
      this.$refs.uploader.click();
    }
  }

  async validateUpload(file: File) {
    try {
      this.loading = true;
      const fileContents = await file.text();
      if (fileContents == undefined) return;
      const result = JSON.parse(fileContents) as SimplePath;
      if (result.Path.length == 0) {
        console.error('path has no points');
      }
      if (result.Name == null || result.Name == '') {
        result.Name = `path_upload_${new Date().toDateString()}`;
      }

      // convert to GPS location to remove duplicate points
      const path = result.Path.map((value) => {
        return { Latitude: value.Lat, Longitude: value.Long, Elevation: value.Alt } as GPSLocation;
      });
      const distinctPath = PathHelper.removeDuplicateSuccessivePoints(path);

      // convert back to simple points
      const distinctSimplePath = distinctPath.map((value) => {
        return { Lat: value.Latitude, Long: value.Longitude, Alt: value.Elevation } as SimplePoint;
      });

      const newPath = new SimplePath();
      newPath.PathID = Guid.create().toString();
      newPath.CustomerID = this.globalStore.accountId;
      newPath.Name = result.Name;
      newPath.Path = distinctSimplePath;
      newPath.WorkAreas = result.WorkAreas;
      newPath.CropAreas = result.CropAreas;
      newPath.FieldID = Guid.createEmpty().toString();
      newPath.PathLengthMeters = 0;
      newPath.StrokeOpacity = 0.5;
      newPath.StrokeWeight = 1;
      newPath.LastModifiedUTC = new Date();
      await this.flightPathService?.insertOrUpdatePath(newPath);
    } catch (error) {
      console.error(error);
    } finally {
      this.recoverPaths();
      this.loading = false;
    }
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
    this.addMqttVisibility(this.globalStore.generalNotificationTopic, () =>
      PubSub.subscribe(this.globalStore.generalNotificationTopic).subscribe({
        next: (data) => this.notificationRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.onResize();
    this.loading = true;
    this.recoverPaths();

    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
    this.emptyField.FieldName = this.$t('none').toString();
  }

  private recoverPaths(){
    this.getAvailableFields()
      .then(() => {
        this.getCurrentPaths()
          .then(() => {
            this.loading = false;
          })
          .catch(() => (this.loading = true));
      })
      .catch(() => (this.loading = true));
  }

  destroyed() {
    if (this.pathStore.edit) this.pathStore.toggleEditable();
    this.removeMqttVisibility();
  }

  private async notificationRecieved(data: any): Promise<void> {
    const response = data.value as string;
    const status = GenericNotifyConvert.toGenericNotification(JSON.stringify(response));

    if (status.Type == NotificationMessageType.NewPath) {
      const entityResponse = await this.flightPathService?.getPathByPathId(status.EntityId, this.globalStore.accountId);
      const entity = JSON.parse(JSON.stringify(entityResponse?.data)) as SimplePath;

      const index = this.pathStore.availablePaths.findIndex((value) => value.PathID == entity.PathID);
      if (index < 0) {
        this.pathStore.paths.push(entity);
      } else {
        this.pathStore.paths.splice(index, 1, entity);
      }
    }
  }

  @Watch('pathStore.paths')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPathsChange(paths: SimplePath[]): void {
    this.plannedPath = this.pathStore.availablePaths;
  }

  @Watch('pathStore.editable')
  onEditableChange(): void {
    this.editMode = this.pathStore.editable;
  }

  @Watch('pathStore.selectedPathLengthInMeters')
  onSelectedPathLengthChange(length: number): void {
    this.selectedPathLength = length;
  }

  @Watch('pathStore.selectedWorkAreaAreaInSqMeters')
  onSelectedWorkAreaAreaChange(area: number): void {
    this.selectedWorkAreaArea = this.pathStore.workAreaAreaHectares;
  }

  @Watch('pathStore.editWorkArea')
  onEditWorkAreaChange(value: boolean) {
    this.editWorkAreaMode = value;
  }

  @Watch('pathStore.editCropArea')
  onEditCropAreaChange(value: boolean) {
    this.editCropAreaMode = value;
  }

  @Watch('selectedField')
  onSelectedFieldChange() {
    this.pathStore.setSelectedField(this.selectedField);
  }

  get pathLengthDisplay() {
    return !this.pathSelected ? '' : `${this.selectedPathLength}m`;
  }
  get fieldNameDisplay() {
    return !this.pathSelected ? '' : this.selectedField.FieldName;
  }
  get lastEditDisplay() {
    return this.pathSelected && this.selectedPath.LastModifiedUTC != this.emptyPath.LastModifiedUTC ? this.formattedDate : '';
  }
  get formattedDate() {
    const date = new Date(this.selectedPath.LastModifiedUTC);
    return date.toLocaleDateString();
  }
  get workAreaDisplay() {
    let area = this.$t('undefined');
    if (this.pathSelected && this.selectedWorkAreaArea > 0) {
      area = `${this.selectedWorkAreaArea}`;
    }
    return !this.pathSelected ? '' : area;
  }
  get workAreaDefined() {
    return this.pathSelected && this.showWorkAreaControls;
  }
  get cropAreaDisplay() {
    let area = this.$t('undefined');
    if (this.pathSelected && this.selectedCropAreaArea > 0) {
      area = `${this.selectedCropAreaArea}`;
    }
    return !this.pathSelected ? '' : area;
  }
  get cropAreaDefined() {
    return this.pathSelected && this.showCropAreaControls;
  }


  async getAvailableFields() {
    if (!this.flightPathService) return;

    const response = await this.flightPathService.getAvailableFields(this.globalStore.accountId);
    const fields = FieldOperationsModel.toModelCollection(JSON.stringify(response.data));

    this.fields = fields;
    this.fields = this.fields.sort((a, b) => SortHelper.compareStrings(a.FieldName as string, b.FieldName as string));
  }

  async getCurrentPaths() {
    if (!this.flightPathService) return;
    const storedPaths = await this.flightPathService.getPathsByCustomerId(this.globalStore.accountId);
    this.pathStore.setAvailablePaths(SimplePath.toModelCollection(JSON.stringify(storedPaths.data)));
  }

  isLinked(path: SimplePath) {
    return path.FieldID != null && Guid.isGuid(path.FieldID) && path.FieldID != Guid.EMPTY;
  }

  async selectPath(path: SimplePath) {
    if (path.PathID == this.selectedPath.PathID) return;
    if (!this.flightPathService) return;
    if (this.pathStore.edit) return;
    this.loading = true;
    try {
      const response = await this.flightPathService.getPathByPathId(path.PathID, this.globalStore.accountId);
      const simplePath = SimplePath.toModel(JSON.stringify(response.data));
      this.pathSelected = true;
      this.selectedPath = simplePath;
      this.pathStore.setSelectedPath(simplePath);
      this.updateSelected(simplePath.PathID);
      // handle paths that do not have a field associated with them
      const fieldIndex = this.fields.findIndex((element) => element.FieldID == this.selectedPath.FieldID);
      if (fieldIndex == -1) {
        this.selectedField = new FieldOperationsModel();
      } else {
        this.selectedField = this.fields[fieldIndex];
      }

      this.pathStore.setSelectedField(this.selectedField);
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  editPath() {
    this.simplifyFactor = 1;
    this.isSimplifyingPath = false;
    if (this.pathSelected) {
      this.pathStore.toggleEditable();
      this.newPathName = this.selectedPath.Name;
      this.showWorkAreaControls = this.pathStore.currentWorkArea.length > 0;
      this.showCropAreaControls = this.pathStore.currentCropArea.length > 0;
    }
  }

  simplifyPath() {
    const points: ISimplifyLatLongPoint[] = [];
    this.pathStore.selectedPath?.Path.forEach((val) => {
      points.push({ latitude: val.Lat, longitude: val.Long });
    });

    const simplifiedPath = SimplifyLL(points, this.simplifyFactor * this.metersToDegreesFactor, true);

    const z = SimplePath.toModel(this.selectedPath.fromModelToJson());
    if (z != null) {
      z.Path = simplifiedPath.map((val) => {
        return { Lat: val.latitude, Long: val.longitude, Alt: 0 };
      });
    }

    this.pathStore.setSimplifiedPath(z);
  }

  revertSimplifiedPath() {
    this.pathStore.revertSimplifiedPath();
    this.isSimplifyingPath = false;
  }

  cancelEdit() {
    const fieldIndex = this.fields.findIndex((element) => element.FieldID == this.selectedPath.FieldID);
    if (fieldIndex == -1) {
      this.selectedField = new FieldOperationsModel();
    } else {
      this.selectedField = this.fields[fieldIndex];
    }

    this.pathStore.toggleEditable();
    this.pathStore.triggerRevertPath();
    this.pathStore.setCurrentWorkArea(this.selectedPath.WorkAreas[0].map((value) => new window.google.maps.LatLng(value.Lat, value.Long)));
    this.pathStore.triggerRevertWorkArea();
    this.pathStore.setCurrentCropArea(this.selectedPath.CropAreas[0].map((value) => new window.google.maps.LatLng(value.Lat, value.Long)));
    this.pathStore.triggerRevertCropArea();
    this.addPathMode = false;
  }

  newPath() {
    this.pathStore.toggleEditable();
    this.clearPathInfo();
    this.addPathMode = true;
    this.pathStore.setAddPathMode(this.addPathMode);
  }

  cancelNewPath() {
    this.pathSelected = false;
    this.pathStore.toggleEditable();
    this.addPathMode = false;
    this.pathStore.setAddPathMode(this.addPathMode);
    this.clearPathInfo();
  }

  clearPathInfo() {
    this.pathStore.triggerClearMap();
    this.pathStore.setSelectedPathLength(0);
    this.selectedField = new FieldOperationsModel();
    this.selectedPath = new SimplePath();
    this.newPathName = '';
  }

  validateNewPathInfo() {
    return this.newPathName.length != 0 && this.pathStore.currentPath.length != 0;
  }

  async savePath() {
    if (!this.flightPathService) return;

    if (this.addPathMode) {
      if (!this.validateNewPathInfo()) {
        alert(this.$t('views.paths.alert.validation'));
        return;
      }
      this.selectedPath.Name = this.newPathName;
      this.selectedPath.CustomerID = this.globalStore.accountId;
      this.selectedPath.PathID = Guid.create().toString();
    } else {
      this.selectedPath.Name = this.newPathName;
      this.editPath();
    }

    const newWorkArea: SimplePoint[] = this.pathStore.workArea.map((value) => ({ Lat: value.lat(), Long: value.lng(), Alt: 0 }));
    if (this.selectedPath.WorkAreas == undefined) {
      this.selectedPath.WorkAreas = [];
    }

    if (this.selectedPath.WorkAreas.length == 0) {
      this.selectedPath.WorkAreas.push(newWorkArea);
    } else {
      this.selectedPath.WorkAreas[0] = newWorkArea;
    }

    const newCropArea: SimplePoint[] = this.pathStore.cropArea.map((value) => ({ Lat: value.lat(), Long: value.lng(), Alt: 0 }));
    if (this.selectedPath.CropAreas == undefined) {
      this.selectedPath.CropAreas = [];
    }

    if (this.selectedPath.CropAreas.length == 0) {
      this.selectedPath.CropAreas.push(newCropArea);
    } else {
      this.selectedPath.CropAreas[0] = newCropArea;
    }

    // must convert SimplePath[] to GPSLocation[] to remove the duplicate points, and then back to SimplePath[]
    this.selectedPath.Path = PathHelper.removeDuplicateSuccessivePoints(
      this.selectedPath.Path.map((value) => {
        return { Latitude: value.Lat, Longitude: value.Long, Elevation: value.Alt } as GPSLocation;
      })
    ).map((value) => {
      return { Lat: value.Latitude, Long: value.Longitude, Alt: value.Elevation } as SimplePoint;
    });

    // take the points from googlemap with are in 2d, and find the closest known altitude for each one using the source 3d data set
    this.selectedPath.Path = AltitudeFinder.Find(
      this.selectedPath.Path,
      this.pathStore.currentPath.map((val) => ({ Lat: val.lat(), Long: val.lng(), Alt: 0 }))
    );
    this.selectedPath.FieldID = this.selectedField.FieldID;

    const finalPath = SimplePath.toModel(this.selectedPath.fromModelToJson());

    if (this.addPathMode) this.cancelNewPath();

    await this.flightPathService.insertOrUpdatePath(finalPath);
    await this.getCurrentPaths();

    if (this.pathStore.isSelectedPathSimplified) {
      // update selected path to the new version that includes any changes made to the simplified line
      this.pathStore.setSelectedPath(this.selectedPath);
    }
  }

  async deletePath() {
    if (!this.flightPathService) return;

    await this.flightPathService.deletePathByPathId(this.selectedPath.PathID, this.globalStore.accountId);
    await this.getCurrentPaths();
    this.pathStore.triggerClearMap();
    this.selectedPath = new SimplePath();
    this.pathSelected = false;
    this.selectedPathLength = 0;
    this.deletePathDialog = false;
  }

  editWorkArea(edit: boolean) {
    if(edit && this.pathStore.editCropArea) { this.cancelEditCropArea(); }
    this.tempWorkArea = this.pathStore.workArea.map((value) => new window.google.maps.LatLng(value.lat(), value.lng()));
    this.showWorkAreaControls = edit;
    this.pathStore.setEditWorkArea(edit);
  }

  cancelEditWorkArea() {
    this.pathStore.setCurrentWorkArea(this.tempWorkArea);
    this.pathStore.triggerRevertWorkArea();
    this.pathStore.setEditWorkArea(false);
    if (this.tempWorkArea.length == 0) this.showWorkAreaControls = false;
  }

  saveWorkArea() {
    if (this.pathStore.workArea.length > 0) {
      this.showWorkAreaControls = true;
    }

    this.pathStore.setEditWorkArea(false);
  }

  deleteWorkArea() {
    this.showWorkAreaControls = false;
    this.pathStore.triggerWorkAreaDelete();
    this.pathStore.setCurrentWorkArea([]);
    this.deleteWorkAreaDialog = false;
  }

  editCropArea(edit: boolean) {
    if(edit && this.pathStore.editWorkArea) { this.cancelEditWorkArea(); }
    this.tempCropArea = this.pathStore.cropArea.map((value) => new window.google.maps.LatLng(value.lat(), value.lng()));
    this.showCropAreaControls = edit;
    this.pathStore.setEditCropArea(edit);
  }

  cancelEditCropArea() {
    this.pathStore.setCurrentCropArea(this.tempCropArea);
    this.pathStore.triggerRevertCropArea();
    this.pathStore.setEditCropArea(false);
    if (this.tempCropArea.length == 0) this.showCropAreaControls = false;
  }

  saveCropArea() {
    if (this.pathStore.cropArea.length > 0) {
      this.showCropAreaControls = true;
    }

    this.pathStore.setEditCropArea(false);
  }

  deleteCropArea() {
    this.showCropAreaControls = false;
    this.pathStore.triggerCropAreaDelete();
    this.pathStore.setCurrentCropArea([]);
    this.deleteCropAreaDialog = false;
  }
}
